/**
 * Real 2D preview for the Layer Properties → Preview tab.
 *
 * Spins up a small, self-contained MapLibre map so the operator can SEE the
 * data (or at least its footprint) even when the layer is invisible in the main
 * 3D scene — the fastest way to tell "is this a render bug, or is the data just
 * somewhere else / empty?". It reuses the engine's PMTiles protocol, basemap
 * registry, and canvas colour resolver so it tracks the rest of the app.
 *
 * Best-effort + defensive: browser/WebGL guarded, every map mutation is wrapped
 * so a missing source-layer or a CORS-blocked tile degrades to "just the
 * footprint" (or a text fallback) instead of throwing into the panel.
 */

import { useEffect, useRef, useState } from 'react';
import maplibregl, { type Map as MLMap } from 'maplibre-gl';
import { tokens } from '@/design';
import { ensurePmtilesProtocol } from '@/engines/maplibre/MapLibreAdapter';
import { applyBasemap, DEFAULT_BASEMAP_ID } from '@/engines/maplibre/basemaps';
import { resolveVectorFallbackColor } from '@/design/tokenResolver';
import { normalizeTitilerSourceHost } from '@/utils/titilerSourceHost';
import { resolveHillshadeTileUrl } from '@/utils/hillshade';

export interface LayerPreviewMapProps {
  readonly bbox: readonly [number, number, number, number] | null;
  readonly center: readonly [number, number] | null;
  readonly pmtiles?: { readonly archiveUrl: string; readonly sourceLayer: string | null } | null;
  /**
   * XYZ raster tiles to draw over the basemap — used to show the ACTUAL data
   * for terrain DEMs / COG rasters (e.g. a TiTiler `.../{z}/{x}/{y}.png` URL),
   * not just a footprint outline.
   */
  readonly raster?: { readonly tiles: string; readonly tileSize?: number; readonly opacity?: number } | null;
  readonly height?: number;
}

/** True when a bbox is a usable lon/lat footprint (not projected / degenerate). */
export function isGeographicBbox(
  b: readonly [number, number, number, number] | null | undefined,
): b is [number, number, number, number] {
  if (!b) return false;
  const [minX, minY, maxX, maxY] = b;
  return (
    [minX, minY, maxX, maxY].every((n) => Number.isFinite(n)) &&
    minX >= -180 &&
    maxX <= 180 &&
    minY >= -90 &&
    maxY <= 90 &&
    maxX > minX &&
    maxY > minY
  );
}

/** Whether there is anything worth drawing a live map for. */
export function hasPreviewableGeometry(
  bbox: readonly [number, number, number, number] | null | undefined,
  pmtiles?: { archiveUrl: string } | null,
): boolean {
  return Boolean(pmtiles?.archiveUrl) || isGeographicBbox(bbox);
}

/**
 * TiTiler often runs in Docker in local dev. If the embedded COG `url=` points
 * to `localhost`, TiTiler resolves that to the tile-server container/process,
 * not the converter output server. See `@/utils/titilerSourceHost`.
 */
// normalizeTitilerSourceHost imported from @/utils/titilerSourceHost

export function LayerPreviewMap({ bbox, center, pmtiles, raster, height = 200 }: LayerPreviewMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Latest props read inside the (key-gated) effect, avoiding array-identity churn.
  const propsRef = useRef({ bbox, center, pmtiles, raster });
  propsRef.current = { bbox, center, pmtiles, raster };
  const [failed, setFailed] = useState(false);

  const bboxKey = bbox ? bbox.join(',') : '';
  const centerKey = center ? center.join(',') : '';
  const pmKey = `${pmtiles?.archiveUrl ?? ''}|${pmtiles?.sourceLayer ?? ''}`;
  const rasterKey = raster?.tiles ?? '';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const el = containerRef.current;
    if (!el) return;

    const { bbox: b, center: c, pmtiles: pm, raster: rs } = propsRef.current;
    let map: MLMap | null = null;
    let observer: ResizeObserver | null = null;

    try {
      ensurePmtilesProtocol();
      const accent = resolveVectorFallbackColor();
      const geo = isGeographicBbox(b);
      const startCenter: [number, number] = c
        ? [c[0], c[1]]
        : geo
          ? [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2]
          : [0, 20];

      map = new maplibregl.Map({
        container: el,
        style: { version: 8, sources: {}, layers: [] },
        center: startCenter,
        zoom: c || geo ? 12 : 1,
        attributionControl: false,
        interactive: true,
        // Keep a hillshade preview shaded like the main map (utils/hillshade.ts).
        transformRequest: (url: string) => {
          const resolved = resolveHillshadeTileUrl(url);
          return resolved === url ? undefined : { url: resolved };
        },
      });
      const m = map;

      // Tile / source errors (missing source-layer, CORS, 404) are expected for
      // a best-effort preview — swallow them rather than surfacing in the panel.
      m.on('error', () => {});

      m.on('load', () => {
        try {
          applyBasemap(m, DEFAULT_BASEMAP_ID);
        } catch {
          /* basemap is optional context */
        }

        // Real raster data (terrain DEM / COG) on top of the basemap so the
        // operator sees the ACTUAL coverage, not just an outline.
        if (rs?.tiles) {
          try {
            m.addSource('lp-raster', {
              type: 'raster',
              tiles: [normalizeTitilerSourceHost(rs.tiles)],
              tileSize: rs.tileSize ?? 256,
            });
            m.addLayer({
              id: 'lp-raster',
              type: 'raster',
              source: 'lp-raster',
              paint: { 'raster-opacity': rs.opacity ?? 1 },
            });
          } catch {
            /* raster overlay is optional */
          }
        }

        if (pm?.archiveUrl) {
          try {
            m.addSource('lp-pmtiles', { type: 'vector', url: `pmtiles://${pm.archiveUrl}` });
            const sl = pm.sourceLayer ?? undefined;
            if (sl) {
              // One layer per geometry family so polygons, lines, and points all
              // show regardless of what the archive actually contains.
              m.addLayer({
                id: 'lp-pm-fill',
                type: 'fill',
                source: 'lp-pmtiles',
                'source-layer': sl,
                paint: { 'fill-color': accent, 'fill-opacity': 0.25 },
              });
              m.addLayer({
                id: 'lp-pm-line',
                type: 'line',
                source: 'lp-pmtiles',
                'source-layer': sl,
                paint: { 'line-color': accent, 'line-width': 1.5 },
              });
              m.addLayer({
                id: 'lp-pm-circle',
                type: 'circle',
                source: 'lp-pmtiles',
                'source-layer': sl,
                paint: {
                  'circle-color': accent,
                  'circle-radius': 3,
                  'circle-stroke-width': 1,
                  'circle-stroke-color': tokens.color.canvas.light,
                },
              });
            }
          } catch {
            /* pmtiles overlay is optional */
          }
        }

        if (geo) {
          const ring: [number, number][] = [
            [b[0], b[1]],
            [b[2], b[1]],
            [b[2], b[3]],
            [b[0], b[3]],
            [b[0], b[1]],
          ];
          try {
            m.addSource('lp-footprint', {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: { type: 'Polygon', coordinates: [ring] },
              },
            });
            m.addLayer({
              id: 'lp-footprint-fill',
              type: 'fill',
              source: 'lp-footprint',
              paint: { 'fill-color': accent, 'fill-opacity': pm ? 0.04 : 0.12 },
            });
            m.addLayer({
              id: 'lp-footprint-line',
              type: 'line',
              source: 'lp-footprint',
              paint: { 'line-color': accent, 'line-width': 2, 'line-dasharray': [2, 1] },
            });
          } catch {
            /* footprint optional */
          }
          try {
            m.fitBounds(
              [
                [b[0], b[1]],
                [b[2], b[3]],
              ],
              { padding: 18, duration: 0, maxZoom: 18 },
            );
          } catch {
            /* fit optional */
          }
        }
      });

      observer = new ResizeObserver(() => {
        try {
          m.resize();
        } catch {
          /* ignore */
        }
      });
      observer.observe(el);
    } catch {
      setFailed(true);
    }

    return () => {
      try {
        observer?.disconnect();
      } catch {
        /* ignore */
      }
      try {
        map?.remove();
      } catch {
        /* ignore */
      }
    };
  }, [bboxKey, centerKey, pmKey, rasterKey]);

  if (failed) {
    return (
      <div
        data-testid="layer-preview-map-fallback"
        style={{
          width: '100%',
          height,
          display: 'grid',
          placeItems: 'center',
          borderRadius: tokens.radius.md,
          border: tokens.border.thin,
          background: tokens.color.surface[2],
          color: tokens.color.ink.muted,
          fontSize: tokens.font.size.xs,
        }}
      >
        Live preview unavailable in this environment.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid="layer-preview-map"
      style={{
        width: '100%',
        height,
        borderRadius: tokens.radius.md,
        overflow: 'hidden',
        border: tokens.border.thin,
        background: tokens.color.surface[2],
      }}
    />
  );
}
